/**
 * WebGL Performance Profiler
 * 高度なパフォーマンス分析とボトルネック検出
 */

import * as THREE from 'three';
import { webglLogger } from '@/lib/utils/logger';

export interface ProfileMetrics {
  fps: number;
  frameTime: number;
  triangles: number;
  drawCalls: number;
  textureMemory: number;
  geometryMemory: number;
  shaderPrograms: number;
  renderTime: number;
  updateTime: number;
  memoryPressure: 'low' | 'medium' | 'high';
  recommendations: string[];
}

export interface ProfileConfig {
  enabled: boolean;
  sampleInterval: number; // frames between samples
  maxSamples: number;
  thresholds: {
    fps: number;
    frameTime: number;
    triangles: number;
    drawCalls: number;
    memory: number;
  };
}

export class WebGLProfiler {
  private config: ProfileConfig;
  private samples: ProfileMetrics[] = [];
  private frameCount = 0;
  private lastSampleTime = 0;
  private enabled = false;

  constructor(config: Partial<ProfileConfig> = {}) {
    this.config = {
      enabled: process.env.NODE_ENV === 'development',
      sampleInterval: 60, // Every 60 frames (1 second at 60fps)
      maxSamples: 100,
      thresholds: {
        fps: 55,
        frameTime: 18, // ~60fps target
        triangles: 100000,
        drawCalls: 100,
        memory: 100 // MB
      },
      ...config
    };
    this.enabled = this.config.enabled;
  }

  public enable(): void {
    this.enabled = true;
    webglLogger.info('WebGL Profiler enabled');
  }

  public disable(): void {
    this.enabled = false;
    webglLogger.info('WebGL Profiler disabled');
  }

  public startFrame(): number {
    return performance.now();
  }

  public endFrame(
    startTime: number,
    renderer: THREE.WebGLRenderer,
    updateTime: number = 0
  ): void {
    if (!this.enabled) return;

    this.frameCount++;
    
    if (this.frameCount % this.config.sampleInterval === 0) {
      const now = performance.now();
      const frameTime = now - startTime;
      const fps = 1000 / frameTime;
      
      const info = renderer.info;
      const metrics: ProfileMetrics = {
        fps: Math.round(fps * 10) / 10,
        frameTime: Math.round(frameTime * 100) / 100,
        triangles: info.render.triangles,
        drawCalls: info.render.calls,
        textureMemory: info.memory.textures,
        geometryMemory: info.memory.geometries,
        shaderPrograms: info.programs?.length || 0,
        renderTime: frameTime - updateTime,
        updateTime,
        memoryPressure: this.calculateMemoryPressure(info),
        recommendations: this.generateRecommendations(fps, frameTime, info)
      };

      this.addSample(metrics);
    }
  }

  private calculateMemoryPressure(info: THREE.WebGLInfo): 'low' | 'medium' | 'high' {
    const totalMemory = info.memory.textures + info.memory.geometries;
    
    if (totalMemory > this.config.thresholds.memory * 2) return 'high';
    if (totalMemory > this.config.thresholds.memory) return 'medium';
    return 'low';
  }

  private generateRecommendations(
    fps: number,
    frameTime: number,
    info: THREE.WebGLInfo
  ): string[] {
    const recommendations: string[] = [];

    if (fps < this.config.thresholds.fps) {
      recommendations.push('Low FPS detected - consider reducing scene complexity');
    }

    if (frameTime > this.config.thresholds.frameTime) {
      recommendations.push('High frame time - optimize update logic or rendering');
    }

    if (info.render.triangles > this.config.thresholds.triangles) {
      recommendations.push('High triangle count - implement LOD or reduce geometry complexity');
    }

    if (info.render.calls > this.config.thresholds.drawCalls) {
      recommendations.push('Too many draw calls - consider instancing or batching');
    }

    if (info.memory.textures > 50) {
      recommendations.push('High texture memory usage - compress textures or reduce resolution');
    }

    return recommendations;
  }

  private addSample(metrics: ProfileMetrics): void {
    this.samples.push(metrics);

    if (this.samples.length > this.config.maxSamples) {
      this.samples.shift();
    }

    // Log performance issues
    if (metrics.recommendations.length > 0) {
      webglLogger.warn('Performance issues detected:', {
        fps: metrics.fps,
        recommendations: metrics.recommendations
      });
    }
  }

  public getReport(): {
    current: ProfileMetrics | null;
    average: Partial<ProfileMetrics>;
    worst: Partial<ProfileMetrics>;
    samples: ProfileMetrics[];
  } {
    if (this.samples.length === 0) {
      return { current: null, average: {}, worst: {}, samples: [] };
    }

    const current = this.samples[this.samples.length - 1];
    
    const average: Partial<ProfileMetrics> = {
      fps: this.average('fps'),
      frameTime: this.average('frameTime'),
      triangles: this.average('triangles'),
      drawCalls: this.average('drawCalls'),
      renderTime: this.average('renderTime'),
      updateTime: this.average('updateTime')
    };

    const worst: Partial<ProfileMetrics> = {
      fps: Math.min(...this.samples.map(s => s.fps)),
      frameTime: Math.max(...this.samples.map(s => s.frameTime)),
      triangles: Math.max(...this.samples.map(s => s.triangles)),
      drawCalls: Math.max(...this.samples.map(s => s.drawCalls))
    };

    return { current, average, worst, samples: this.samples };
  }

  private average(key: keyof ProfileMetrics): number {
    const values = this.samples.map(s => s[key] as number).filter(v => typeof v === 'number');
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  public reset(): void {
    this.samples = [];
    this.frameCount = 0;
    webglLogger.info('WebGL Profiler reset');
  }

  public exportData(): string {
    return JSON.stringify({
      config: this.config,
      report: this.getReport(),
      timestamp: new Date().toISOString()
    }, null, 2);
  }
}

// Global profiler instance
export const webglProfiler = new WebGLProfiler();